import { useCallback, useEffect, useState } from 'react';
import { isRenderable, isViewableImage, type ArtifactRecord, type ShareLink } from '@reef/schema';
import type { RunSummary, WorkflowSummary } from '../api.ts';
import { CommentThread } from '../components/CommentThread.tsx';
import { request } from '../http.ts';

/**
 * What the runs produced, as opposed to how they thought.
 *
 * This is the screen that exists because a step's transcript was the only place
 * output ever lived: finding last Tuesday's incident writeup meant remembering
 * which step of which run had happened to write it, and reading it meant
 * scrolling a conversation. Here the unit is the document — named, versioned,
 * with a sentence saying what it is — and the run it came from is a detail on
 * the card rather than the thing you have to know first.
 *
 * **Metadata and contents are two requests, deliberately.** An artifact has no
 * ceiling — a full table export is a legitimate one — so a list that fetched
 * bodies to render cards would get slower with every report anybody kept.
 * Nothing here loads content until somebody opens something.
 */
export function ArtifactsView({ workflows }: { workflows: WorkflowSummary[] }) {
  const [workflowId, setWorkflowId] = useState<string | null>(workflows[0]?.id ?? null);
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [runId, setRunId] = useState<string | null>(null);
  const [artifacts, setArtifacts] = useState<ArtifactRecord[] | null>(null);
  const [selected, setSelected] = useState<ArtifactRecord | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ link: ShareLink; token: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!workflowId) return;
    setRunId(null);
    artifactsApi
      .listRuns(workflowId)
      .then((result) => setRuns(result.runs))
      .catch(() => setRuns([]));
  }, [workflowId]);

  const refresh = useCallback(() => {
    if (!workflowId) return;
    // Narrowed to a run when one is picked and to the workflow otherwise. The
    // endpoint refuses an unfiltered list, so there is no "everything" state to
    // fall into while the workflow list is still loading.
    artifactsApi
      .listArtifacts(runId ? { runId } : { workflowId })
      .then((result) => setArtifacts(result.artifacts))
      .catch((caught) => setError((caught as Error).message));
  }, [workflowId, runId]);

  useEffect(refresh, [refresh]);

  const open = async (artifact: ArtifactRecord) => {
    setSelected(artifact);
    setContent(null);
    setIssued(null);
    setError(null);
    if (!isRenderable(artifact.contentType)) return;

    setBusy(true);
    try {
      setContent(await artifactsApi.fetchContent(artifact.id));
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const share = async (artifact: ArtifactRecord) => {
    setBusy(true);
    setError(null);
    try {
      setIssued(await artifactsApi.mintShareLink(artifact.id));
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="body">
      <aside className="sidebar">
        <p className="section-title">Workflow</p>
        <select
          value={workflowId ?? ''}
          onChange={(event) => {
            setWorkflowId(event.target.value);
            setSelected(null);
          }}
        >
          {workflows.map((workflow) => (
            <option key={workflow.id} value={workflow.id}>
              {workflow.name}
            </option>
          ))}
        </select>

        <p className="section-title" style={{ marginTop: '1rem' }}>
          Run
        </p>
        <button className="list-item" aria-current={runId === null} onClick={() => setRunId(null)}>
          Every run
          <small>Newest first, across the whole workflow</small>
        </button>
        {runs.map((run) => (
          <button
            key={run.id}
            className="list-item"
            aria-current={run.id === runId}
            onClick={() => setRunId(run.id)}
          >
            {run.id}
            <small>
              {run.status} · {new Date(run.createdAt).toLocaleString()}
            </small>
          </button>
        ))}
      </aside>

      <main className="main">
        <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>Artifacts</h1>
        <p className="hint" style={{ marginTop: 0 }}>
          Things a run produced and somebody wanted to keep. A name and a version are the address —
          a run that regenerates a report adds a version rather than erasing the one you quoted.
        </p>

        {error && (
          <div className="banner danger">
            <p style={{ margin: 0 }}>{error}</p>
          </div>
        )}

        {issued && (
          <div className="banner warn">
            <h3>Copy this token now</h3>
            <p style={{ marginTop: 0 }}>
              It is shown once. Reef stores only a hash of it, so it cannot be shown again — if it
              is lost, revoke this link and mint another. Anyone holding it can read this artifact
              without an account on the workflow.
            </p>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{issued.link.url}</pre>
            <div className="row wrap">
              <button onClick={() => void navigator.clipboard?.writeText(issued.link.url)}>
                Copy link
              </button>
              <button onClick={() => setIssued(null)}>Dismiss</button>
              <span className="hint">
                Expires {issued.link.expiresAt ? new Date(issued.link.expiresAt).toLocaleDateString() : 'never'}
              </span>
            </div>
          </div>
        )}

        {artifacts === null && <p className="hint">Loading…</p>}

        {artifacts?.length === 0 && (
          <div className="empty">
            Nothing kept from {runId ? 'this run' : 'this workflow'} yet. A step produces one by
            calling <code>reef.artifact()</code>.
          </div>
        )}

        <div className="stack">
          {artifacts?.map((artifact) => (
            <div className="card" key={artifact.id}>
              <div className="row wrap">
                <strong style={{ fontSize: '0.9rem' }}>{artifact.name}</strong>
                <span className="pill accent">v{artifact.version}</span>
                <span className="pill">{artifact.contentType.split(';')[0]}</span>
                {!isRenderable(artifact.contentType) && !isViewableImage(artifact.contentType) && (
                  <span className="pill warn">download</span>
                )}
              </div>

              {artifact.summary && <p style={{ margin: '0.4rem 0 0.5rem' }}>{artifact.summary}</p>}

              <p className="hint" style={{ margin: '0 0 0.6rem' }}>
                {formatBytes(artifact.byteSize)} · {new Date(artifact.createdAt).toLocaleString()} ·{' '}
                {artifact.createdBy ?? 'an unattended run'}
                {artifact.stepId && ` · step ${artifact.stepId}`} · run {artifact.runId}
              </p>

              <div className="row wrap">
                <button
                  className="primary"
                  disabled={busy}
                  onClick={() => void open(artifact)}
                  aria-current={selected?.id === artifact.id}
                >
                  {isRenderable(artifact.contentType) || isViewableImage(artifact.contentType)
                    ? 'Open'
                    : 'Details'}
                </button>
                <button disabled={busy} onClick={() => void share(artifact)}>
                  Share
                </button>
              </div>

              {selected?.id === artifact.id && (
                <div className="stack" style={{ marginTop: '0.75rem' }}>
                  {/*
                    Rendered as text rather than as markdown or HTML, and that is
                    a security decision rather than a missing feature: an
                    artifact is written by a model out of whatever a tool
                    returned, so treating it as markup would let a row in
                    somebody's database put a link on this page.
                  */}
                  {isViewableImage(artifact.contentType) ? (
                    /*
                     * An image is shown, not linked. The inner loop's visual
                     * check is told to attach the screenshot, and a verdict
                     * about pixels nobody can look at is not a verdict — which
                     * is exactly what a download link makes it. Loaded from the
                     * content endpoint rather than fetched into state: the
                     * bytes are for the browser to decode, and reading a PNG
                     * into a string to hand back to an `<img>` would corrupt it.
                     */
                    <img
                      src={contentUrl(artifact.id)}
                      alt={artifact.summary ?? artifact.name}
                      style={{ maxWidth: '100%', border: '1px solid var(--line)', borderRadius: 4 }}
                    />
                  ) : isRenderable(artifact.contentType) ? (
                    content === null ? (
                      <p className="hint">Loading contents…</p>
                    ) : (
                      <pre style={{ whiteSpace: 'pre-wrap', overflowX: 'auto' }}>{content}</pre>
                    )
                  ) : (
                    <p className="hint" style={{ margin: 0 }}>
                      Reef will not render {artifact.contentType} inline —{' '}
                      <a href={contentUrl(artifact.id)} target="_blank" rel="noreferrer">
                        open it directly
                      </a>{' '}
                      and your browser decides what to do with it.
                    </p>
                  )}

                  <CommentThread subjectKind="artifact" subjectId={artifact.id} />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="row">
          <button onClick={refresh}>Refresh</button>
        </div>
      </main>
    </div>
  );
}

/**
 * A local client for the artifact endpoints.
 *
 * Kept in this file rather than added to `api.ts` because every one of these
 * calls has a single caller — this screen — and a shared client that collects
 * per-screen methods stops being the shape of the API and becomes the union of
 * whatever anybody needed. The session cookie authorizes them all, hence
 * `credentials: 'include'`.
 */
const artifactsApi = {
  listArtifacts: (filter: { runId?: string; workflowId?: string }) => {
    const query = new URLSearchParams();
    if (filter.runId) query.set('runId', filter.runId);
    if (filter.workflowId) query.set('workflowId', filter.workflowId);
    return request<{ artifacts: ArtifactRecord[] }>(`/artifacts?${query.toString()}`);
  },

  listRuns: (workflowId: string) =>
    request<{ runs: RunSummary[] }>(`/workflows/${encodeURIComponent(workflowId)}/runs`),

  /**
   * The bytes, as text. Not JSON, so it does not go through `request` — the
   * endpoint answers with the artifact's own content type and parsing that as
   * JSON would fail on every markdown file in the workspace.
   */
  fetchContent: async (id: string): Promise<string> => {
    const response = await fetch(contentUrl(id), { credentials: 'include' });
    if (!response.ok) {
      throw new Error(
        response.status === 410
          ? 'This artifact is recorded but its contents are no longer stored.'
          : `Could not read the artifact (${response.status}).`,
      );
    }
    return response.text();
  },

  mintShareLink: (id: string) =>
    request<{ link: ShareLink; token: string }>('/collaborate/share-links', {
      method: 'POST',
      body: JSON.stringify({ subjectKind: 'artifact', subjectId: id }),
    }),
};

function contentUrl(id: string): string {
  return `/api/v1/artifacts/${encodeURIComponent(id)}/content`;
}

/** Bytes as somebody would say them aloud, so a 4 MB export is obviously not a note. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
